import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, User, Phone, Mail, MapPin, Home, MessageSquare, Loader2, Truck, Check, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useCartStore, formatCOP } from '../store/cartStore';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, getSubtotal, getTotalBags, clearCart } = useCartStore();
  
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    region: '',
    postal_code: '',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [shippingCost, setShippingCost] = useState(null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [calculatingShipping, setCalculatingShipping] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  
  const totalBags = getTotalBags();
  const subtotal = getSubtotal();
  const debouncedCity = useDebounce(customer.city, 600);
  const total = subtotal + (shippingCost || 0);
  
  useEffect(() => {
    if (items.length === 0 && !orderCompleted) {
      navigate('/');
    }
  }, [items, navigate, orderCompleted]);
  
  useEffect(() => {
    if (!debouncedCity || debouncedCity.length < 2 || totalBags === 0) {
      setShippingCost(null);
      setShippingInfo(null);
      return;
    }
    
    const calculate = async () => {
      setCalculatingShipping(true);
      try {
        const response = await axios.post(`${API}/shipping/calculate`, {
          city: debouncedCity,
          total_bags: totalBags
        });
        setShippingCost(response.data.shipping_cost);
        setShippingInfo(response.data);
      } catch (error) {
        setShippingCost(null);
        setShippingInfo(null);
      } finally {
        setCalculatingShipping(false);
      }
    };
    
    calculate();
  }, [debouncedCity, totalBags]);
  
  const handleChange = (field, value) => {
    setCustomer(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const validate = () => {
    const newErrors = {};
    if (!customer.name || customer.name.trim().length < 2) newErrors.name = 'Nombre requerido';
    if (!customer.phone || customer.phone.trim().length < 7) newErrors.phone = 'Teléfono requerido';
    if (!customer.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) newErrors.email = 'Correo válido requerido';
    if (!customer.address || customer.address.trim().length < 5) newErrors.address = 'Dirección requerida';
    if (!customer.city || customer.city.trim().length < 2) newErrors.city = 'Ciudad requerida';
    if (!customer.region || customer.region.trim().length < 2) newErrors.region = 'Departamento requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const openWompiWidget = (paymentData, orderNumber) => {
    try {
      // Validate widget is loaded
      if (typeof window.WidgetCheckout === 'undefined') {
        toast.error('El sistema de pago no está disponible. Por favor recarga la página e intenta de nuevo.');
        navigate(`/order-confirmation/${orderNumber}`);
        return;
      }
      
      // Validate required payment data
      if (!paymentData.public_key || !paymentData.reference || !paymentData.amount_in_cents || !paymentData.signature) {
        console.error('Datos de pago incompletos:', paymentData);
        toast.error('Los datos de pago están incompletos. Tu pedido fue creado, un asesor se comunicará contigo.');
        navigate(`/order-confirmation/${orderNumber}`);
        return;
      }
      
      // Ensure amount is positive integer
      const amountInCents = parseInt(paymentData.amount_in_cents, 10);
      if (!Number.isFinite(amountInCents) || amountInCents <= 0) {
        toast.error('El monto del pedido no es válido.');
        navigate(`/order-confirmation/${orderNumber}`);
        return;
      }
      
      // Build widget config with only valid fields
      const widgetConfig = {
        currency: paymentData.currency || 'COP',
        amountInCents: amountInCents,
        reference: String(paymentData.reference),
        publicKey: String(paymentData.public_key),
        signature: {
          integrity: String(paymentData.signature)
        },
        redirectUrl: `${window.location.origin}/order-confirmation/${orderNumber}`
      };
      
      // Add optional customer data if present
      if (paymentData.customer_email) {
        widgetConfig.customerData = {
          email: paymentData.customer_email,
          fullName: paymentData.customer_data?.full_name || '',
          phoneNumber: paymentData.customer_data?.phone_number || '',
          phoneNumberPrefix: '+57'
        };
      }
      
      // Add shipping address if present
      if (paymentData.shipping_address) {
        widgetConfig.shippingAddress = {
          addressLine1: paymentData.shipping_address.address_line_1 || '',
          city: paymentData.shipping_address.city || '',
          region: paymentData.shipping_address.region || '',
          country: paymentData.shipping_address.country || 'CO',
          phoneNumber: paymentData.shipping_address.phone_number || '',
          name: paymentData.shipping_address.name || '',
          postalCode: paymentData.shipping_address.postal_code || '000000'
        };
      }
      
      // Create widget instance safely
      let checkout;
      try {
        checkout = new window.WidgetCheckout(widgetConfig);
      } catch (widgetInitError) {
        console.error('Error al inicializar Wompi widget:', widgetInitError);
        toast.error('No se pudo abrir el sistema de pago. Tu pedido fue creado, te contactaremos pronto.');
        navigate(`/order-confirmation/${orderNumber}`);
        return;
      }
      
      // Open widget with error handling
      try {
        checkout.open((result) => {
          try {
            const transaction = result?.transaction;
            if (transaction) {
              // Payment attempted - navigate to confirmation
              navigate(`/order-confirmation/${orderNumber}`);
            } else {
              // User closed widget without paying
              toast.info('Puedes completar tu pago después. Ve al detalle de tu pedido.');
              navigate(`/order-confirmation/${orderNumber}`);
            }
          } catch (callbackError) {
            console.error('Error en callback de Wompi:', callbackError);
            navigate(`/order-confirmation/${orderNumber}`);
          }
        });
      } catch (openError) {
        console.error('Error al abrir Wompi widget:', openError);
        toast.error('No se pudo abrir la ventana de pago. Tu pedido fue creado, te contactaremos pronto.');
        navigate(`/order-confirmation/${orderNumber}`);
      }
    } catch (unexpectedError) {
      // Catch any unexpected error
      console.error('Error inesperado en Wompi:', unexpectedError);
      toast.error('Ocurrió un error inesperado. Tu pedido fue creado, te contactaremos pronto.');
      navigate(`/order-confirmation/${orderNumber}`);
    }
  };
  
  const handleSubmit = async () => {
    // Validation checks
    if (!validate()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    if (shippingCost === null) {
      toast.error('Ingresa tu ciudad para calcular el envío');
      return;
    }
    
    // Verify cart has items
    if (!items || items.length === 0) {
      toast.error('Tu carrito está vacío. Agrega productos antes de continuar.');
      navigate('/');
      return;
    }
    
    // Verify subtotal and total are positive
    if (subtotal <= 0) {
      toast.error('El subtotal del pedido no es válido.');
      return;
    }
    
    if (total <= 0) {
      toast.error('El total del pedido no es válido.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const orderItems = items.map(item => ({
        profile: item.profile,
        grind: item.grind,
        quantity: item.quantity,
        unit_price: item.price
      }));
      
      const response = await axios.post(`${API}/orders`, {
        customer,
        items: orderItems
      });
      
      // Validate response
      if (!response.data || !response.data.success) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      toast.success('¡Pedido creado! Abriendo pago...');
      setOrderCompleted(true);
      
      // Clear cart (deferred to avoid race)
      setTimeout(() => clearCart(), 100);
      
      // Open Wompi widget for payment
      if (response.data.payment && response.data.payment.public_key) {
        setTimeout(() => {
          openWompiWidget(response.data.payment, response.data.order_number);
        }, 400);
      } else {
        // No Wompi configured, go straight to confirmation
        toast.info('El pago se coordinará directamente con nosotros.');
        navigate(`/order-confirmation/${response.data.order_number}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      
      let errorMsg = 'Error al procesar tu pedido. Por favor intenta de nuevo.';
      
      if (error.response) {
        // Server responded with error
        if (error.response.status === 400) {
          errorMsg = error.response.data?.detail || 'Datos del pedido inválidos.';
        } else if (error.response.status === 422) {
          errorMsg = 'Por favor verifica que todos los campos estén completos.';
        } else if (error.response.status === 500) {
          errorMsg = 'Error del servidor. Por favor intenta más tarde.';
        } else if (error.response.data?.detail) {
          errorMsg = error.response.data.detail;
        }
      } else if (error.request) {
        // No response from server
        errorMsg = 'No se pudo conectar con el servidor. Verifica tu conexión.';
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      setSubmitting(false);
    }
  };
  
  if (items.length === 0 && !orderCompleted) return null;
  
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header onCartClick={() => {}} />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#142A1D] hover:text-[#B58A3F] transition-colors mb-8 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la tienda
        </motion.button>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-[#B58A3F] uppercase tracking-[0.3em] text-sm mb-3 font-medium">
            Finalizar Compra
          </p>
          <h1 className="heading-serif text-4xl md:text-5xl text-[#142A1D] tracking-tight font-light">
            Datos de Envío
          </h1>
        </motion.div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-white rounded-2xl border border-[#E5DEC5] p-8 shadow-sm">
              <h2 className="heading-serif text-2xl text-[#142A1D] mb-6 font-light">
                Información de Contacto
              </h2>
              
              <div className="grid md:grid-cols-2 gap-5">
                <FormField icon={User} label="Nombre completo" placeholder="Ej: María González" value={customer.name} onChange={(v) => handleChange('name', v)} error={errors.name} testId="checkout-name" required />
                <FormField icon={Phone} label="Teléfono" placeholder="Ej: 3001234567" value={customer.phone} onChange={(v) => handleChange('phone', v)} error={errors.phone} testId="checkout-phone" type="tel" required />
                <FormField icon={Mail} label="Correo electrónico" placeholder="tu@correo.com" value={customer.email} onChange={(v) => handleChange('email', v)} error={errors.email} testId="checkout-email" type="email" className="md:col-span-2" required />
                <FormField icon={Home} label="Dirección" placeholder="Ej: Calle 123 #45-67" value={customer.address} onChange={(v) => handleChange('address', v)} error={errors.address} testId="checkout-address" className="md:col-span-2" required />
                <FormField icon={MapPin} label="Ciudad" placeholder="Ej: Bogotá" value={customer.city} onChange={(v) => handleChange('city', v)} error={errors.city} testId="checkout-city" required hint="El costo de envío se calcula según tu ubicación y cantidad de bolsas" />
                <FormField icon={MapPin} label="Departamento" placeholder="Ej: Cundinamarca" value={customer.region} onChange={(v) => handleChange('region', v)} error={errors.region} testId="checkout-region" required />
                <div className="md:col-span-2">
                  <label className="block text-[#142A1D] text-sm font-medium mb-2">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-[#B58A3F]" strokeWidth={1.5} />
                      Notas adicionales (opcional)
                    </span>
                  </label>
                  <textarea
                    data-testid="checkout-notes"
                    value={customer.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Instrucciones especiales, referencias, etc."
                    rows={3}
                    className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5DEC5] rounded-xl text-[#142A1D] placeholder:text-[#142A1D]/40 focus:outline-none focus:border-[#B58A3F] transition-colors resize-none"
                  />
                </div>
              </div>
            </div>
            
            {/* Payment Methods Info */}
            <div className="bg-white rounded-2xl border border-[#E5DEC5] p-6 shadow-sm">
              <h3 className="heading-serif text-xl text-[#142A1D] mb-4 font-light flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-[#B58A3F]" strokeWidth={1.5} />
                Métodos de Pago
              </h3>
              <p className="text-sm text-[#142A1D] opacity-70 mb-4">
                Pago seguro procesado por <strong>Wompi</strong>. Todas las transacciones están encriptadas y son 100% seguras.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {['Tarjetas', 'PSE', 'Nequi', 'Daviplata'].map((method) => (
                  <div key={method} className="px-3 py-2 bg-[#F2EBE1] rounded-lg text-center text-[#142A1D] font-medium border border-[#E5DEC5]">
                    {method}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
          
          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-1"
          >
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="bg-white rounded-2xl border border-[#E5DEC5] p-6 shadow-sm">
                <h2 className="heading-serif text-2xl text-[#142A1D] mb-6 font-light">
                  Resumen del Pedido
                </h2>
                
                <div className="space-y-4 mb-6">
                  {items.map((item) => (
                    <div key={item.key} className="flex gap-3 pb-4 border-b border-[#E5DEC5] last:border-0 last:pb-0">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-16 h-16 object-contain rounded-lg bg-[#0a0a0a] flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#142A1D] text-sm mb-1">{item.name}</p>
                        <div className="flex flex-wrap gap-1 mb-1">
                          <span className="inline-block px-2 py-0.5 bg-[#B58A3F]/10 text-[#B58A3F] rounded text-xs font-medium">{item.profile}</span>
                          <span className="inline-block px-2 py-0.5 bg-[#2B4C36]/10 text-[#2B4C36] rounded text-xs font-medium">{item.grind}</span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-[#142A1D] opacity-60">{item.quantity} × {formatCOP(item.price)}</span>
                          <span className="text-sm font-medium text-[#142A1D]">{formatCOP(item.price * item.quantity)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-3 pt-4 border-t border-[#E5DEC5]">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#142A1D] opacity-70">Subtotal ({totalBags} {totalBags === 1 ? 'bolsa' : 'bolsas'})</span>
                    <span className="text-[#142A1D] font-medium">{formatCOP(subtotal)}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-[#142A1D] opacity-70 flex items-center gap-2">
                      <Truck className="w-4 h-4 text-[#B58A3F]" strokeWidth={1.5} />
                      Envío
                    </span>
                    <AnimatePresence mode="wait">
                      {calculatingShipping ? (
                        <motion.span key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-[#142A1D] opacity-50 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Calculando...
                        </motion.span>
                      ) : shippingCost !== null ? (
                        <motion.span key="cost" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="shipping-cost" className="text-[#142A1D] font-medium">
                          {formatCOP(shippingCost)}
                        </motion.span>
                      ) : (
                        <motion.span key="pending" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-[#142A1D] opacity-40 text-xs italic">
                          Ingresa tu ciudad
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  {shippingInfo && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="text-xs text-[#B58A3F] bg-[#B58A3F]/5 rounded-lg p-2 flex items-center gap-2">
                      <Check className="w-3 h-3 flex-shrink-0" strokeWidth={2.5} />
                      <span>
                        {shippingInfo.is_bogota ? `Envío a Bogotá (${totalBags <= 3 ? 'hasta 3' : 'más de 3'} bolsas)` : `Envío nacional (${totalBags <= 3 ? 'hasta 3' : 'más de 3'} bolsas)`}
                      </span>
                    </motion.div>
                  )}
                  
                  <div className="pt-3 border-t border-[#E5DEC5] flex justify-between items-baseline">
                    <span className="text-lg font-medium text-[#142A1D]">Total</span>
                    <span data-testid="checkout-total" className="text-3xl font-light text-[#B58A3F] heading-serif">
                      {formatCOP(total)}
                    </span>
                  </div>
                </div>
                
                <motion.button
                  data-testid="checkout-submit"
                  onClick={handleSubmit}
                  disabled={submitting || shippingCost === null}
                  whileHover={!submitting && shippingCost !== null ? { scale: 1.02, y: -2 } : {}}
                  whileTap={!submitting && shippingCost !== null ? { scale: 0.98 } : {}}
                  className="w-full btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" strokeWidth={1.5} />
                      Pagar {formatCOP(total)}
                    </>
                  )}
                </motion.button>
                
                <p className="text-xs text-[#142A1D] opacity-60 text-center mt-4">
                  Pago seguro con Wompi. Serás redirigido para completar tu pago.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

const FormField = ({ icon: Icon, label, error, testId, className = '', required, hint, ...inputProps }) => {
  return (
    <div className={className}>
      <label className="block text-[#142A1D] text-sm font-medium mb-2">
        <span className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#B58A3F]" strokeWidth={1.5} />}
          {label}
          {required && <span className="text-[#B58A3F]">*</span>}
        </span>
      </label>
      <input
        data-testid={testId}
        {...inputProps}
        onChange={(e) => inputProps.onChange(e.target.value)}
        className={`w-full px-4 py-3 bg-[#FDFBF7] border rounded-xl text-[#142A1D] placeholder:text-[#142A1D]/40 focus:outline-none transition-colors ${
          error ? 'border-red-400 focus:border-red-500' : 'border-[#E5DEC5] focus:border-[#B58A3F]'
        }`}
      />
      {error && (
        <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-red-500 text-xs mt-1">
          {error}
        </motion.p>
      )}
      {hint && !error && (
        <p className="text-[#142A1D] opacity-50 text-xs mt-1">{hint}</p>
      )}
    </div>
  );
};

export default CheckoutPage;
